// web.effects.ts
import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import type { Action } from '@ngrx/store';
import { catchError, map, of, switchMap } from 'rxjs';

import * as WebActions from './web.actions';
import type { ApiResponse } from '../../models/api-response';
import type { Extras } from '../../models/booking';
import type { VehicleFilterOptions } from '../../models/car-filters';
import { ApiService } from '../../services/api.service';
import { WEB_CONFIG } from '../../templates/web/web-config';

@Injectable()
export class WebEffects {
  private readonly actions$ = inject(Actions);
  private readonly apiService = inject(ApiService);

  loadFilters$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WebActions.loadFilters),
      switchMap(({ payload }) =>
        this.apiService
          .post<VehicleFilterOptions>(WEB_CONFIG.getFilters, { ...payload }, true, true)
          .pipe(
            map((response: ApiResponse<VehicleFilterOptions>) => {
              if (response?.statusCode === 200 && response.data) {
                const formattedData = this.formatFilterData(response.data);
                return WebActions.loadFiltersSuccess({ filters: formattedData });
              } else {
                return WebActions.loadFiltersFailure({
                  error: response?.message || 'Unknown error',
                });
              }
            }),
            catchError((error) => of(WebActions.loadFiltersFailure({ error })))
          )
      )
    )
  );

  private formatFilterData(data: VehicleFilterOptions): VehicleFilterOptions {
    return {
      name: data.name?.map((n: string) => n.trim()) || [],
      make: data.make?.map((m: string) => m.trim()) || [],
      transmission: data.transmission?.map((t: string) => this.formatTransmission(t)) || [],
      seats: data.seats || [],
      type: data.type || [],
      fuel_type: data.fuel_type || [],
      model: data.model || [],
    };
  }

  private formatTransmission(value: string): string {
    if (!value) {
      return '';
    }

    const formatted = value.replace(/_/g, ' ');
    const words = formatted
      .split(' ')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());

    return words.length > 1 ? words.join(' / ') : words[0];
  }

  loadLocationAutoComplete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WebActions.loadLocationAutoComplete),
      switchMap(({ searchValue }) => {
        const url = `${WEB_CONFIG.pickUpLocationAutocomplete}?key=${searchValue}`;
        return this.apiService.post<{ list: string[] }>(url, {}, true, true).pipe(
          map((response) => {
            if (response?.statusCode === 200 && Array.isArray(response.data.list)) {
              const uniqueMap = new Map<string, string>();

              response.data.list
                .flatMap((item: string) => item.split(','))
                .map((loc) => loc.trim())
                .filter((loc) => loc.length > 0)
                .forEach((loc) => {
                  const normalized = loc.toLowerCase().replace(/\s+/g, ' ');
                  if (!uniqueMap.has(normalized)) {
                    uniqueMap.set(normalized, loc);
                  }
                });

              const list = Array.from(uniqueMap.values()).map((name) => ({
                name,
              }));

              return WebActions.loadLocationAutoCompleteSuccess({ list });
            } else {
              return WebActions.loadLocationAutoCompleteFailure();
            }
          }),
          catchError(() => of(WebActions.loadLocationAutoCompleteFailure()))
        );
      })
    )
  );

  loadExtras$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WebActions.loadExtras),
      switchMap(() => {
        const url = WEB_CONFIG.getExtrasOptions;
        return this.apiService.post<Extras[]>(url, {}, false, true).pipe(
          map((response: ApiResponse<Extras[]>): Action => {
            if (response?.statusCode === 200) {
              return WebActions.loadExtrasSuccess({ extras: response.data });
            } else {
              return WebActions.loadExtrasFailure({
                error: response?.message || 'Failed to load extras',
              });
            }
          }),
          catchError((error) => of(WebActions.loadExtrasFailure({ error }) as Action))
        );
      })
    )
  );
}
